import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import Accordion from '../../../shared/Accordion';
import '../../../shared/StyledInput.css';

// Tenant-wide settings for the Warehouse module: what the Pick Slip
// prints, and the size Package Labels print at by default (issues #300, #305;
// the sizes themselves are Warehouse › Label Sizes). Moved here from Sales Settings. Backed by the same
// /api/platform-settings store as the other Setup pages, in the same
// collapsible Accordion cards.

function draftFrom(data) {
  return {
    print_sku_cards_on_so: data.print_sku_cards_on_so ?? false,
    box_label_size_id: data.box_label_size_id ? String(data.box_label_size_id) : '',
  };
}

function WarehouseSettingsPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [draft, setDraft] = useState(draftFrom({}));
  const [saved, setSaved] = useState(draftFrom({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [labelSizes, setLabelSizes] = useState([]);
  const set = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    // Label Sizes first: listing them sets the default size for a tenant
    // that has none yet.
    axios.get('/api/label-sizes')
      .then((res) => setLabelSizes(res.data.data || []))
      .catch(() => setLabelSizes([]))
      .then(() => axios.get('/api/platform-settings'))
      .then((res) => {
        const loaded = draftFrom(res.data.data || {});
        setDraft(loaded);
        setSaved(loaded);
      })
      .catch((err) => showError(err, 'Failed to load Warehouse Settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      const res = await axios.put('/api/platform-settings', {
        print_sku_cards_on_so: draft.print_sku_cards_on_so,
        ...(draft.box_label_size_id ? { box_label_size_id: Number(draft.box_label_size_id) } : {}),
      });
      const next = draftFrom(res.data.data || {});
      setDraft(next);
      setSaved(next);
      setSuccess('Warehouse settings saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setDraft(saved);
    setSuccess('');
    if (onNavigate) onNavigate('warehouse-pick');
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 600, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Warehouse Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>Tenant-wide defaults for the Warehouse module.</p>

      <form onSubmit={handleSave} style={{ marginTop: 16 }}>
        <Accordion title="Pick Slip Print" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Appends one card per distinct SKU to the end of a Pick Slip, starting on a
            new page, each carrying a Matrix barcode for that SKU. A laser-printed
            stand-in for a real label while bedding down the Matrix barcode format
            alongside legacy Code128 stock.
          </div>
          <ToggleSwitch checked={draft.print_sku_cards_on_so} onChange={set('print_sku_cards_on_so')} label="Print a card per SKU at the end of the Pick Slip" />
        </Accordion>

        <Accordion title="Package Label" defaultOpen={false}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            The label size the Package Label preview opens at. Staff can choose
            another size there before printing.
          </div>
          <label style={{ fontSize: 13 }}>Default Label Size</label><br />
          <select className="styled-input" value={draft.box_label_size_id} onChange={(e) => set('box_label_size_id')(e.target.value)} style={{ width: 220 }}>
            {!draft.box_label_size_id && <option value="">Choose a size</option>}
            {labelSizes.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>
        </Accordion>

        {success && <div style={{ color: '#2e7d32', marginTop: 10 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default WarehouseSettingsPage;
